import { createContext, type FC, type ReactNode, useContext, useEffect, useState } from 'react';
import type { AirgapAPI } from '@transcend-io/airgap.js-types';

/** A stub for `window.airgap` to be used before airgap is actually loaded */
type PreInitAirgapAPI = Required<Pick<AirgapAPI, 'readyQueue' | 'ready'>>;

/**
 * Add `window.airgap` to the global namespace
 */
declare global {
    interface Window {
        /** airgap.js interface */
        airgap?: PreInitAirgapAPI | AirgapAPI;
    }
}

/**
 * The `airgap` and consent management APIs
 *
 * @example const { airgap } = useConsentManager();
 */
interface ConsentAPI {
    /** The `airgap` consent management instance */
    airgap?: AirgapAPI;
}

interface ConsentProviderProps {
    /** The airgap API instance */
    airgapAPI: AirgapAPI | undefined;

    /** The children of this context provider */
    children: ReactNode;
}

/** The React context by the `useConsentManager()` hook */
// eslint-disable-next-line react-refresh/only-export-components
export const ConsentContext = createContext<ConsentAPI>({});

/**
 * React context provider for `window.airgap` and `window.transcend`
 * @see https://docs.transcend.io/docs/consent/faq
 */
export const ConsentProvider: FC<ConsentProviderProps> = ({ airgapAPI, children }: ConsentProviderProps) => {
    const [airgap, setAirgap] = useState<AirgapAPI | undefined>(airgapAPI);

    // `useEffect` ensures this is only executed in browser
    useEffect(() => {
        // Stub airgap with PreInit API
        if (!self.airgap?.ready) {
            const preInitAirgap: PreInitAirgapAPI = {
                readyQueue: [],
                ready(callback) {
                    this.readyQueue.push(callback);
                },
                ...self.airgap,
            };
            self.airgap = preInitAirgap;
        }

        // Wait for airgap.js core to load, and set it in the React state
        if (!airgap) {
            self.airgap.ready((airgap) => {
                setAirgap(airgap);
            });
        }
    }, [airgap]);

    return <ConsentContext.Provider value={{ airgap }}>{children}</ConsentContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useConsentManager = (): ConsentAPI => useContext(ConsentContext);
